import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Pause, Play, RefreshCw, X } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
  { value: 'finished', label: 'Sent and completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-blue-100 text-blue-700',
  sending: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-teal-100 text-teal-700',
  completed: 'bg-green-100 text-green-700',
  paused: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-700'
};

const EMPTY_FILTERS = { search: '', status: 'all', dateFrom: '', dateTo: '' };
const PAGE_SIZE = 50;

const formatNumber = value => (Number(value) || 0).toLocaleString();
const formatRate = (value, denominator) => (
  denominator > 0 ? `${Math.round((Number(value || 0) / denominator) * 100)}%` : '0%'
);
const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};
const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};
const stepLabel = roundNumber => (
  Number(roundNumber) <= 0 ? 'Initial email' : `Follow-up ${Number(roundNumber) + 1}`
);
const recipientStatusColor = status => {
  if (['sent', 'replied', 'forwarded'].includes(status)) return 'bg-blue-100 text-blue-700';
  if (['bounced', 'failed', 'not_interested'].includes(status)) return 'bg-red-100 text-red-700';
  if (status === 'out_of_office') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
};
const contactType = status => {
  const types = {
    cold: { label: 'Cold', color: 'bg-blue-100 text-blue-700' },
    warm: { label: 'Warm', color: 'bg-amber-100 text-amber-800' },
    active: { label: 'Active', color: 'bg-green-100 text-green-700' }
  };
  return types[status] || types.cold;
};

const FolderPerformancePanel = ({ folder, onCampaignOpen, onCampaignStatusChange, canResume }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [sort, setSort] = useState({ key: 'sent_count', direction: 'desc' });
  const [metricView, setMetricView] = useState({
    isOpen: false,
    metric: '',
    label: '',
    rows: [],
    total: 0,
    offset: 0,
    campaignId: 'all',
    round: 'all',
    sortKey: 'click_count',
    sortDirection: 'desc',
    loading: false,
    exporting: false,
    error: ''
  });

  const requestParams = useMemo(() => {
    const params = {};
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    return params;
  }, [filters]);

  const loadPerformance = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/api/email-campaigns/folders/${folder.id}/performance`, {
        params: requestParams
      });
      if (!response.data.success) throw new Error('Folder performance is unavailable.');
      setPerformance(response.data);
    } catch (requestError) {
      console.error('Failed to load folder performance:', requestError);
      setError(requestError.response?.data?.error || requestError.message || 'Failed to load folder performance.');
    } finally {
      setLoading(false);
    }
  }, [folder.id, requestParams]);

  useEffect(() => {
    const timeoutId = setTimeout(loadPerformance, filters.search ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [filters.search, loadPerformance]);

  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setMetricView(previous => ({ ...previous, isOpen: false }));
  }, [folder.id]);

  useEffect(() => {
    setSelectedCampaignIds(new Set());
  }, [folder.id, requestParams]);

  const sortedCampaigns = useMemo(() => {
    const campaigns = [...(performance?.campaigns || [])];
    return campaigns.sort((first, second) => {
      const firstValue = sort.key === 'start_date'
        ? (first.started_at || first.scheduled_at || first.created_at || '')
        : sort.key === 'name' || sort.key === 'status'
          ? String(first[sort.key] || '').toLowerCase()
          : Number(first[sort.key]) || 0;
      const secondValue = sort.key === 'start_date'
        ? (second.started_at || second.scheduled_at || second.created_at || '')
        : sort.key === 'name' || sort.key === 'status'
          ? String(second[sort.key] || '').toLowerCase()
          : Number(second[sort.key]) || 0;

      if (firstValue === secondValue) return 0;
      const direction = sort.direction === 'asc' ? 1 : -1;
      return firstValue > secondValue ? direction : -direction;
    });
  }, [performance?.campaigns, sort]);

  const changeSort = key => {
    setSort(previous => ({
      key,
      direction: previous.key === key && previous.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const selectedCampaigns = useMemo(
    () => sortedCampaigns.filter(campaign => selectedCampaignIds.has(String(campaign.id))),
    [selectedCampaignIds, sortedCampaigns]
  );
  const allCampaignsSelected = sortedCampaigns.length > 0
    && sortedCampaigns.every(campaign => selectedCampaignIds.has(String(campaign.id)));
  const someCampaignsSelected = selectedCampaigns.length > 0;
  const pauseEligibleCount = selectedCampaigns.filter(campaign => (
    ['scheduled', 'sending', 'sent'].includes(campaign.status)
  )).length;
  const resumeEligibleCount = selectedCampaigns.filter(campaign => campaign.status === 'paused').length;

  const toggleCampaign = campaignId => {
    setSelectedCampaignIds(previous => {
      const next = new Set(previous);
      const selectedId = String(campaignId);
      if (next.has(selectedId)) next.delete(selectedId);
      else next.add(selectedId);
      return next;
    });
  };

  const toggleAllCampaigns = () => {
    setSelectedCampaignIds(previous => {
      const next = new Set(previous);
      if (allCampaignsSelected) {
        sortedCampaigns.forEach(campaign => next.delete(String(campaign.id)));
      } else {
        sortedCampaigns.forEach(campaign => next.add(String(campaign.id)));
      }
      return next;
    });
  };

  const updateSelectedCampaigns = async action => {
    const eligibleCount = action === 'pause' ? pauseEligibleCount : resumeEligibleCount;
    const actionLabel = action === 'pause' ? 'pause' : 'resume';
    const completedLabel = action === 'pause' ? 'paused' : 'resumed';
    if (eligibleCount === 0) return;

    if (action === 'resume' && !canResume) {
      alert('Connect a Gmail account in Settings before you resume campaigns.');
      return;
    }

    const unavailableCount = selectedCampaigns.length - eligibleCount;
    const unavailableMessage = unavailableCount > 0
      ? ` ${unavailableCount} selected campaign${unavailableCount === 1 ? ' is' : 's are'} not available for this action.`
      : '';
    if (!window.confirm(`${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} ${eligibleCount} selected campaign${eligibleCount === 1 ? '' : 's'}?${unavailableMessage}`)) {
      return;
    }

    try {
      setBulkAction(action);
      const response = await axios.put(`/api/email-campaigns/folders/${folder.id}/status`, {
        action,
        campaign_ids: selectedCampaigns.map(campaign => campaign.id)
      });
      if (!response.data.success) throw new Error(`The selected campaigns could not be ${completedLabel}.`);

      const updatedCount = Number(response.data.updated_count) || 0;
      const skippedLockedCount = Number(response.data.skipped_locked_count) || 0;
      const ineligibleCount = Math.max(selectedCampaigns.length - (Number(response.data.eligible_count) || 0), 0);
      let message = `${updatedCount} campaign${updatedCount === 1 ? '' : 's'} ${completedLabel}.`;
      if (skippedLockedCount > 0) {
        message += ` ${skippedLockedCount} campaign${skippedLockedCount === 1 ? ' is' : 's are'} still processing.`;
      }
      if (ineligibleCount > 0) {
        message += ` ${ineligibleCount} campaign${ineligibleCount === 1 ? ' was' : 's were'} not available for this action.`;
      }
      alert(message);
      setSelectedCampaignIds(new Set());
      await loadPerformance();
      if (onCampaignStatusChange) await onCampaignStatusChange();
    } catch (requestError) {
      console.error(`Failed to ${actionLabel} selected campaigns:`, requestError);
      alert(`Failed to ${actionLabel} selected campaigns: ` + (requestError.response?.data?.error || requestError.message));
    } finally {
      setBulkAction('');
    }
  };

  const loadMetricRows = useCallback(async (
    metric,
    label,
    offset = 0,
    campaignId = 'all',
    round = 'all'
  ) => {
    try {
      setMetricView(previous => ({
        ...previous,
        isOpen: true,
        metric,
        label,
        offset,
        campaignId,
        round,
        sortKey: previous.metric === metric
          ? previous.sortKey
          : metric === 'opens' ? 'open_count' : metric === 'clicks' ? 'click_count' : 'name',
        sortDirection: previous.metric === metric ? previous.sortDirection : 'desc',
        loading: true,
        error: ''
      }));
      const response = await axios.get(`/api/email-campaigns/folders/${folder.id}/performance/contacts`, {
        params: {
          ...requestParams,
          metric,
          limit: PAGE_SIZE,
          offset,
          ...(campaignId !== 'all' ? { campaignId } : {}),
          ...(round !== 'all' ? { round } : {})
        }
      });
      if (!response.data.success) throw new Error('Folder activity is unavailable.');
      setMetricView(previous => ({
        ...previous,
        rows: response.data.rows || [],
        total: Number(response.data.total) || 0,
        loading: false
      }));
    } catch (requestError) {
      console.error('Failed to load folder activity:', requestError);
      setMetricView(previous => ({
        ...previous,
        loading: false,
        error: requestError.response?.data?.error || requestError.message || 'Failed to load folder activity.'
      }));
    }
  }, [folder.id, requestParams]);

  const metricRows = useMemo(() => {
    const rows = [...metricView.rows];
    return rows.sort((first, second) => {
      const firstValue = metricView.sortKey === 'name'
        ? `${first.first_name || ''} ${first.last_name || ''}`.trim().toLowerCase()
        : metricView.sortKey === 'campaign_name'
          ? String(first.campaign_name || '').toLowerCase()
          : metricView.sortKey === 'schedule'
            ? String(first.next_scheduled_at || first.event_at || '')
            : Number(first[metricView.sortKey]) || 0;
      const secondValue = metricView.sortKey === 'name'
        ? `${second.first_name || ''} ${second.last_name || ''}`.trim().toLowerCase()
        : metricView.sortKey === 'campaign_name'
          ? String(second.campaign_name || '').toLowerCase()
          : metricView.sortKey === 'schedule'
            ? String(second.next_scheduled_at || second.event_at || '')
            : Number(second[metricView.sortKey]) || 0;
      if (firstValue === secondValue) return 0;
      const direction = metricView.sortDirection === 'asc' ? 1 : -1;
      return firstValue > secondValue ? direction : -direction;
    });
  }, [metricView.rows, metricView.sortDirection, metricView.sortKey]);

  const changeMetricSort = sortKey => {
    setMetricView(previous => ({
      ...previous,
      sortKey,
      sortDirection: previous.sortKey === sortKey && previous.sortDirection === 'desc' ? 'asc' : 'desc'
    }));
  };

  const exportMetricCsv = async () => {
    try {
      setMetricView(previous => ({ ...previous, exporting: true }));
      const response = await axios.get(`/api/email-campaigns/folders/${folder.id}/performance/contacts`, {
        params: {
          ...requestParams,
          metric: metricView.metric,
          limit: 50000,
          offset: 0,
          export: true,
          ...(metricView.campaignId !== 'all' ? { campaignId: metricView.campaignId } : {}),
          ...(metricView.round !== 'all' ? { round: metricView.round } : {})
        }
      });
      if (!response.data.success) throw new Error('CSV data is unavailable.');

      const csvRows = response.data.rows || [];
      const escapeCsv = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const headers = ['Full Name', 'Email', 'Company', 'Location', 'Status', 'Type', 'Opens', 'Clicks', 'Round', 'Schedule', 'Campaign Name'];
      const rows = csvRows.map(row => [
        [row.first_name, row.last_name].filter(Boolean).join(' '),
        row.email,
        row.company,
        [row.location, row.country].filter(Boolean).join(', '),
        row.event_status || row.recipient_status,
        row.contact_status,
        Number(row.open_count) || 0,
        Number(row.click_count) || 0,
        Number(row.round_number) || 0,
        row.next_scheduled_at || row.event_at,
        row.campaign_name
      ]);
      const csv = [headers, ...rows].map(row => row.map(escapeCsv).join(',')).join('\n');
      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${folder.name}-${metricView.label}-recipients.csv`
        .replace(/[^a-z0-9._-]+/gi, '-');
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error('Failed to export folder activity:', exportError);
      alert('Failed to export folder activity: ' + (exportError.response?.data?.error || exportError.message));
    } finally {
      setMetricView(previous => ({ ...previous, exporting: false }));
    }
  };

  const summary = performance?.summary || {};
  const delivered = Number(summary.delivered_count) || 0;
  const sent = Number(summary.sent_count) || 0;
  const summaryCards = [
    { label: 'Campaigns', value: summary.campaign_count },
    { label: 'Unique Contacts', value: summary.unique_contacts, metric: 'contacts' },
    { label: 'Campaign Recipients', value: summary.campaign_recipients, metric: 'recipients' },
    { label: 'Emails Sent', value: sent, metric: 'sent' },
    { label: 'Delivered', value: delivered, metric: 'delivered' },
    { label: 'Bounces', value: summary.bounce_count, rate: formatRate(summary.bounce_count, sent), metric: 'bounces' },
    { label: 'Unique Opens', value: summary.unique_open, rate: formatRate(summary.unique_open, delivered), metric: 'opens' },
    { label: 'Unique Clicks', value: summary.unique_click, rate: formatRate(summary.unique_click, delivered), metric: 'clicks' },
    { label: 'Replies', value: summary.replied_count, rate: formatRate(summary.replied_count, sent), metric: 'replies' }
  ];

  const SortHeader = ({ sortKey, children, align = 'left' }) => (
    <th className={`whitespace-nowrap px-3 py-3 text-xs font-medium uppercase text-gray-500 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button type="button" onClick={() => changeSort(sortKey)} className="inline-flex items-center gap-1 hover:text-gray-800">
        {children}
        {sort.key === sortKey && (sort.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
      </button>
    </th>
  );

  return (
    <div className="mb-4 rounded-lg border bg-white shadow-sm">
      <div className="border-b p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Folder Performance</p>
            <h3 className="mt-1 text-lg font-semibold text-gray-900">{folder.name}</h3>
            <p className="text-sm text-gray-500">Compare campaigns and inspect combined activity.</p>
          </div>
          <button type="button" onClick={loadPerformance} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <input
            type="search"
            value={filters.search}
            onChange={event => setFilters(previous => ({ ...previous, search: event.target.value }))}
            placeholder="Filter folder statistics by campaign..."
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <select
            value={filters.status}
            onChange={event => setFilters(previous => ({ ...previous, status: event.target.value }))}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">
            <span>From</span>
            <input type="date" value={filters.dateFrom} onChange={event => setFilters(previous => ({ ...previous, dateFrom: event.target.value }))} className="min-w-0 flex-1" />
          </label>
          <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">
            <span>To</span>
            <input type="date" value={filters.dateTo} onChange={event => setFilters(previous => ({ ...previous, dateTo: event.target.value }))} className="min-w-0 flex-1" />
          </label>
        </div>
      </div>

      {error ? (
        <div className="p-6 text-center">
          <p className="text-sm text-red-700">{error}</p>
          <button type="button" onClick={loadPerformance} className="mt-3 rounded-lg bg-red-600 px-3 py-2 text-sm text-white">Try Again</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-px bg-gray-200 sm:grid-cols-3 xl:grid-cols-5">
            {summaryCards.map(card => (
              <button
                key={card.label}
                type="button"
                disabled={!card.metric || loading}
                onClick={() => card.metric && loadMetricRows(card.metric, card.label)}
                className="bg-white p-4 text-center hover:bg-blue-50 disabled:cursor-default disabled:hover:bg-white"
              >
                <p className="text-xl font-semibold text-gray-900">{loading ? '—' : formatNumber(card.value)}</p>
                {card.rate && <p className="text-xs font-medium text-blue-600">{card.rate}</p>}
                <p className="text-xs text-gray-500">{card.label}</p>
              </button>
            ))}
          </div>

          <div className="border-t p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-semibold text-gray-900">Campaign Breakdown</h4>
                <p className="text-xs text-gray-500">Use the checkboxes for bulk actions. Select a row to open its campaign card.</p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {someCampaignsSelected && (
                  <>
                    <span className="text-sm font-medium text-gray-700">{selectedCampaigns.length} selected</span>
                    <button
                      type="button"
                      disabled={Boolean(bulkAction) || pauseEligibleCount === 0}
                      onClick={() => updateSelectedCampaigns('pause')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Pause size={14} />
                      Pause ({pauseEligibleCount})
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(bulkAction) || resumeEligibleCount === 0}
                      onClick={() => updateSelectedCampaigns('resume')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Play size={14} />
                      Resume ({resumeEligibleCount})
                    </button>
                  </>
                )}
                <span className="text-sm text-gray-500">{sortedCampaigns.length} campaign{sortedCampaigns.length === 1 ? '' : 's'}</span>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <table className="min-w-[1250px] w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-11 px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allCampaignsSelected}
                        ref={element => {
                          if (element) element.indeterminate = someCampaignsSelected && !allCampaignsSelected;
                        }}
                        onChange={toggleAllCampaigns}
                        aria-label="Select all campaigns in this list"
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <SortHeader sortKey="name">Campaign Name</SortHeader>
                    <SortHeader sortKey="status">Status</SortHeader>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">Current Step</th>
                    <SortHeader sortKey="start_date">Start Date</SortHeader>
                    <SortHeader sortKey="total_recipients" align="right">Recipients</SortHeader>
                    <SortHeader sortKey="sent_count" align="right">Sent</SortHeader>
                    <th className="px-3 py-3 text-right text-xs font-medium uppercase text-gray-500">Delivered</th>
                    <SortHeader sortKey="bounce_count" align="right">Bounces</SortHeader>
                    <SortHeader sortKey="unique_open" align="right">Opens</SortHeader>
                    <SortHeader sortKey="unique_click" align="right">Clicks</SortHeader>
                    <SortHeader sortKey="replied_count" align="right">Replies</SortHeader>
                  </tr>
                </thead>
                <tbody className="divide-y bg-white">
                  {sortedCampaigns.map(campaign => {
                    const campaignSent = Number(campaign.sent_count) || 0;
                    const campaignBounces = Number(campaign.bounce_count) || 0;
                    const campaignDelivered = Math.max(campaignSent - campaignBounces, 0);
                    return (
                      <tr
                        key={campaign.id}
                        onClick={() => onCampaignOpen(campaign)}
                        className={`cursor-pointer hover:bg-blue-50 ${selectedCampaignIds.has(String(campaign.id)) ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-3 py-3" onClick={event => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedCampaignIds.has(String(campaign.id))}
                            onChange={() => toggleCampaign(campaign.id)}
                            aria-label={`Select ${campaign.name}`}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="max-w-64 px-3 py-3 text-sm font-medium text-blue-700"><span className="block truncate" title={campaign.name}>{campaign.name}</span></td>
                        <td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}>{campaign.status ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1) : 'Unknown'}</span></td>
                        <td className="px-3 py-3 text-sm text-gray-600">{stepLabel(campaign.current_round)} <span className="text-xs text-gray-400">({Math.min((Number(campaign.current_round) || 0) + 1, Number(campaign.template_count) || 1)}/{Number(campaign.template_count) || 1})</span></td>
                        <td className="px-3 py-3 text-sm text-gray-600">{formatDate(campaign.started_at || campaign.scheduled_at || campaign.created_at)}</td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaign.total_recipients)}</td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaignSent)}</td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaignDelivered)}</td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaignBounces)} <span className="block text-xs text-gray-400">{formatRate(campaignBounces, campaignSent)}</span></td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaign.unique_open)} <span className="block text-xs text-gray-400">{formatRate(campaign.unique_open, campaignDelivered)}</span></td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaign.unique_click)} <span className="block text-xs text-gray-400">{formatRate(campaign.unique_click, campaignDelivered)}</span></td>
                        <td className="px-3 py-3 text-right text-sm">{formatNumber(campaign.replied_count)} <span className="block text-xs text-gray-400">{formatRate(campaign.replied_count, campaignSent)}</span></td>
                      </tr>
                    );
                  })}
                  {!loading && sortedCampaigns.length === 0 && (
                    <tr><td colSpan="12" className="px-4 py-8 text-center text-sm text-gray-500">No campaigns match these folder filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {metricView.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-7xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Folder Details: {folder.name} — {metricView.label}</h2>
              <button type="button" onClick={() => setMetricView(previous => ({ ...previous, isOpen: false }))} className="text-gray-400 hover:text-gray-600" aria-label="Close folder details">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-hidden rounded-lg border">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-gray-50 px-4 py-3">
                <h3 className="font-semibold text-gray-900">Recipients ({formatNumber(metricView.total)})</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    Campaign:
                    <select
                      value={metricView.campaignId}
                      onChange={event => loadMetricRows(metricView.metric, metricView.label, 0, event.target.value, metricView.round)}
                      className="max-w-72 rounded border border-gray-300 bg-white px-2 py-1 text-sm"
                    >
                      <option value="all">All Campaigns</option>
                      {(performance?.campaigns || []).map(campaign => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    Round:
                    <select
                      value={metricView.round}
                      onChange={event => loadMetricRows(metricView.metric, metricView.label, 0, metricView.campaignId, event.target.value)}
                      className="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
                    >
                      <option value="all">All Rounds</option>
                      {Array.from({ length: Math.max(1, ...(performance?.campaigns || []).map(campaign => Number(campaign.template_count) || 1)) + 1 }, (_, round) => (
                        <option key={round} value={round}>Round {round}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {metricView.loading ? (
                <div className="flex items-center justify-center py-16 text-gray-500"><RefreshCw className="mr-2 animate-spin" size={18} /> Loading recipients...</div>
              ) : metricView.error ? (
                <div className="py-16 text-center text-red-700">{metricView.error}</div>
              ) : (
                <div className="max-h-96 overflow-auto">
                  <table className="w-full min-w-[1250px] text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="w-12 p-3 text-left font-medium text-gray-500">#</th>
                        <th className="p-3 text-left font-medium text-gray-900"><button type="button" onClick={() => changeMetricSort('name')} className="inline-flex items-center gap-1">Full Name{metricView.sortKey === 'name' && (metricView.sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</button></th>
                        <th className="p-3 text-left font-medium text-gray-900">Email</th>
                        <th className="p-3 text-left font-medium text-gray-900">Company</th>
                        <th className="p-3 text-left font-medium text-gray-900">Location</th>
                        <th className="p-3 text-left font-medium text-gray-900">Status</th>
                        <th className="p-3 text-left font-medium text-gray-900">Type</th>
                        <th className="p-3 text-center font-medium text-gray-900"><button type="button" onClick={() => changeMetricSort('open_count')} className="inline-flex items-center gap-1">Opens{metricView.sortKey === 'open_count' && (metricView.sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</button></th>
                        <th className="p-3 text-center font-medium text-gray-900"><button type="button" onClick={() => changeMetricSort('click_count')} className="inline-flex items-center gap-1">Clicks{metricView.sortKey === 'click_count' && (metricView.sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</button></th>
                        <th className="p-3 text-left font-medium text-gray-900">Round</th>
                        <th className="p-3 text-left font-medium text-gray-900"><button type="button" onClick={() => changeMetricSort('schedule')} className="inline-flex items-center gap-1">Schedule{metricView.sortKey === 'schedule' && (metricView.sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</button></th>
                        <th className="p-3 text-left font-medium text-gray-900"><button type="button" onClick={() => changeMetricSort('campaign_name')} className="inline-flex items-center gap-1">Campaign Name{metricView.sortKey === 'campaign_name' && (metricView.sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</button></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {metricRows.map((row, index) => {
                        const type = contactType(row.contact_status);
                        const status = row.event_status || row.recipient_status || 'pending';
                        return (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="w-12 p-3 font-mono text-gray-500">{metricView.offset + index + 1}</td>
                            <td className="max-w-44 p-3"><span className="block truncate" title={[row.first_name, row.last_name].filter(Boolean).join(' ')}>{[row.first_name, row.last_name].filter(Boolean).join(' ') || 'Unknown Contact'}</span></td>
                            <td className="p-3 text-blue-600">{row.email || '—'}</td>
                            <td className="max-w-56 p-3"><span className="block truncate" title={row.company}>{row.company || 'N/A'}</span></td>
                            <td className="max-w-48 p-3"><span className="block truncate" title={[row.location, row.country].filter(Boolean).join(', ')}>{[row.location, row.country].filter(Boolean).join(', ') || 'N/A'}</span></td>
                            <td className="p-3"><span className={`inline-block rounded-full px-2 py-1 text-xs ${recipientStatusColor(status)}`}>{String(status).replace(/_/g, ' ').replace(/^./, character => character.toUpperCase())}</span></td>
                            <td className="p-3"><span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${type.color}`}>{type.label}</span></td>
                            <td className="p-3 text-center"><span className="inline-block rounded bg-blue-50 px-2 py-1 font-medium text-blue-700">{formatNumber(row.open_count)}</span></td>
                            <td className="p-3 text-center"><span className="inline-block rounded bg-purple-50 px-2 py-1 font-medium text-purple-700">{formatNumber(row.click_count)}</span></td>
                            <td className="p-3">{Number(row.round_number) || 0}</td>
                            <td className="p-3 text-gray-600">{formatDateTime(row.next_scheduled_at || row.event_at)}</td>
                            <td className="max-w-72 p-3"><span className="block truncate" title={row.campaign_name}>{row.campaign_name || '—'}</span></td>
                          </tr>
                        );
                      })}
                      {metricRows.length === 0 && <tr><td colSpan="12" className="p-10 text-center text-gray-500">No matching recipients were found.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">Email totals can exceed this recipient list because one recipient can receive multiple sequence steps.</p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" disabled={metricView.offset === 0 || metricView.loading} onClick={() => loadMetricRows(metricView.metric, metricView.label, Math.max(metricView.offset - PAGE_SIZE, 0), metricView.campaignId, metricView.round)} className="rounded border px-3 py-2 text-sm disabled:opacity-50">Previous</button>
                <span className="text-xs text-gray-500">{metricView.total ? `${metricView.offset + 1}-${Math.min(metricView.offset + PAGE_SIZE, metricView.total)} of ${metricView.total}` : '0 results'}</span>
                <button type="button" disabled={metricView.offset + PAGE_SIZE >= metricView.total || metricView.loading} onClick={() => loadMetricRows(metricView.metric, metricView.label, metricView.offset + PAGE_SIZE, metricView.campaignId, metricView.round)} className="rounded border px-3 py-2 text-sm disabled:opacity-50">Next</button>
                <button type="button" onClick={exportMetricCsv} disabled={metricView.exporting || metricView.loading} className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">{metricView.exporting ? 'Exporting...' : 'Export to CSV'}</button>
                <button type="button" onClick={() => setMetricView(previous => ({ ...previous, isOpen: false }))} className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderPerformancePanel;
